import { useState } from 'react';
import { apiFetch } from '../apiBase';
import { fmtReward, fmtWager, fmtDate, fmtDateTime } from '../lib/wager';

const STATUS = {
  open: { label: 'Open', color: '#F6BB42', bg: 'rgba(246,187,66,.12)' },
  paid: { label: 'Paid', color: '#5ee08a', bg: 'rgba(94,224,138,.12)' },
  rejected: { label: 'Rejected', color: '#ff544a', bg: 'rgba(255,84,74,.12)' },
};

function Message({ m }) {
  if (m.author === 'system') {
    return <div className="text-[12px] text-[#888899] italic px-1">{m.body}</div>;
  }
  const staff = m.author === 'staff';
  return (
    <div className={`flex ${staff ? 'justify-start' : 'justify-end'}`}>
      <div
        className="max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm whitespace-pre-wrap break-words"
        style={staff ? { background: 'rgba(255,84,74,.12)', border: '1px solid rgba(255,84,74,.3)', color: '#fff' } : { background: '#1d1d28', color: '#e6e6ea' }}
      >
        <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: staff ? '#ff8f86' : '#7d7d8c' }}>
          {staff ? 'FreeMansLive staff' : 'You'} · {fmtDateTime(m.created_at * 1000)}
        </div>
        {m.body}
      </div>
    </div>
  );
}

function Ticket({ t, open, onToggle, onChange }) {
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const s = STATUS[t.status] || STATUS.open;

  async function send() {
    setBusy(true);
    setError('');
    try {
      const res = await apiFetch(`/api/benefits/claims/${t.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: reply }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Could not send');
      setReply('');
      onChange(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div id={`ticket-${t.id}`} className="glass-panel rounded-xl overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center gap-3 p-4 text-left cursor-pointer hover:bg-white/[.02]">
        <span className="font-mono-brand text-xs text-[#7d7d8c]">#{t.id}</span>
        <div className="flex-1 min-w-0">
          <div className="text-white font-bold text-sm">
            {t.platformName} — milestone {t.tier}{' '}
            <span className="font-mono-brand" style={{ color: '#5ee08a' }}>{fmtReward(t.unit, t.owed)}</span>
          </div>
          <div className="text-[11px] text-[#7d7d8c] mt-0.5">
            Cycle {fmtDate(t.cycleStart)} – {fmtDate(t.cycleEnd)} · {fmtWager(t.unit, t.wagered)} wagered · updated {fmtDateTime(t.updatedAt * 1000)}
          </div>
        </div>
        <span className="font-mono-brand text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full" style={{ color: s.color, background: s.bg }}>
          {s.label}
        </span>
        <span className="text-[#7d7d8c] text-sm">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div className="border-t border-[#24242e] p-4 flex flex-col gap-3">
          <div className="text-[11px] text-[#7d7d8c]">
            Account: <span className="text-[#c9c9d6]">{t.account}</span> · Payout: <span className="text-[#c9c9d6]">{t.payout}</span>
          </div>
          {t.messages?.map((m) => <Message key={m.id} m={m} />)}
          {t.status === 'open' ? (
            <div className="flex flex-col gap-2 mt-1">
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                maxLength={1000}
                rows={2}
                placeholder="Reply to staff"
                className="w-full rounded-lg bg-[#181820] border border-[#353535] px-3 py-2.5 text-sm text-white placeholder:text-[#7d7d8c]"
              />
              {error && <p className="text-xs text-[#ff544a]">{error}</p>}
              <button onClick={send} disabled={busy || !reply.trim()} className="btn-primary self-end rounded-lg px-5 py-2 text-sm disabled:opacity-50">
                {busy ? 'Sending…' : 'Send'}
              </button>
            </div>
          ) : (
            <p className="text-xs text-[#7d7d8c]">This ticket is closed.</p>
          )}
        </div>
      )}
    </div>
  );
}

/** The player's milestone claim tickets, newest activity first, each with its thread. */
export default function ClaimTickets({ tickets, openId, setOpenId, onChange }) {
  if (!tickets.length) return null;
  return (
    <section className="max-w-3xl mx-auto mb-16">
      <h2 className="font-display-lg text-2xl text-white mb-4">My Claims</h2>
      <div className="flex flex-col gap-3">
        {tickets.map((t) => (
          <Ticket key={t.id} t={t} open={openId === t.id} onToggle={() => setOpenId(openId === t.id ? null : t.id)} onChange={onChange} />
        ))}
      </div>
    </section>
  );
}
